/**
 * docs/BILLING.md — small helpers over a list of packages, shared by the pricing cards and the
 * pages that hold their chosen period.
 */

import type { TFunction } from 'i18next';

import type { Plan, PlanPrice } from './types';

/** The periods any plan is sold for, shortest first. */
export function planMonths(plans: Plan[]): number[] {
  const all = new Set<number>();
  for (const plan of plans) for (const price of plan.prices) all.add(price.months);
  return [...all].sort((a, b) => a - b);
}

/** The period a page opens on: monthly when it is sold, else the shortest there is. */
export function defaultMonths(plans: Plan[]): number {
  const months = planMonths(plans);
  return months.includes(1) ? 1 : (months[0] ?? 1);
}

export function priceFor(plan: Plan, months: number): PlanPrice | null {
  return plan.prices.find((price) => price.months === months) ?? null;
}

/** `diamond` is above them all: a hidden package an administrator assigns (the topbar's chip). */
export type PlanTier = 'diamond' | 'gold' | 'silver' | 'bronze';
const TIERS: readonly PlanTier[] = ['gold', 'silver', 'bronze'];

/**
 * A package's colour: gold for the top paid one (highest `sort_order`), then silver, then bronze
 * for it and everything below. A free package (no prices) and one not in the list have none.
 */
export function planTier(planId: string | null | undefined, plans: Plan[] | undefined): PlanTier | null {
  if (!planId || !plans) return null;
  const paid = plans
    .filter((plan) => plan.prices.length > 0)
    .sort((a, b) => b.sort_order - a.sort_order);
  const index = paid.findIndex((plan) => plan.id === planId);
  return index < 0 ? null : TIERS[Math.min(index, TIERS.length - 1)];
}

/** "Aylık", "Yıllık" or "3 ay". */
export function periodLabel(t: TFunction, months: number): string {
  if (months === 1) return t('billing.period.monthly');
  if (months === 12) return t('billing.period.yearly');
  return t('billing.period.months', { count: months });
}
